import React, { createContext, useContext, useState, useCallback } from 'react';
import { MdCheckCircle, MdInfo, MdCancel } from 'react-icons/md';

export type ToastType = 'success' | 'info' | 'error';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let toastCounter = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++toastCounter;
    setToasts(prev => [...prev, { id, message, type }]);
    // Auto-dismiss
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-start max-w-xs p-4 pr-5 rounded-lg shadow-lg animate-fade-in-down
            ${toast.type === 'success' ? 'bg-emerald-500 text-white' : ''}
            ${toast.type === 'info' ? 'bg-blue-500 text-white' : ''}
            ${toast.type === 'error' ? 'bg-red-500 text-white' : ''}`}
          >
            {toast.type === 'success' && <MdCheckCircle className="w-5 h-5 mr-2 text-white" />}    
            {toast.type === 'info' && <MdInfo className="w-5 h-5 mr-2 text-white" />}    
            {toast.type === 'error' && <MdCancel className="w-5 h-5 mr-2 text-white" />}    
            <span className="text-sm font-medium text-white">{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}; 